import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import type { ARQrParams } from '../qr/types';
import { SCALE_TABLE, modelUrl, bodyTextureUrl, faceTextureUrl } from './constants';

let dracoLoader: DRACOLoader | null = null;
let gltfLoader: GLTFLoader | null = null;

function getLoader(): GLTFLoader {
  if (!gltfLoader) {
    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(dracoLoader);
  }
  return gltfLoader;
}

const textureLoader = new THREE.TextureLoader();

function loadTextureAsync(url: string): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    textureLoader.load(
      url,
      (tex) => {
        // 本体 GameEngine.ts の設定と完全一致させる:
        //   - flipY=false: GLB の UV 座標系（左下原点）と外部 PNG の整合
        //   - wrap=Repeat: 表情アトラスの offset スライドを使うため
        //   - colorSpace=sRGB: ガンマ補正
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.flipY = false;
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        resolve(tex);
      },
      undefined,
      (err) => reject(err),
    );
  });
}

export type BuiltCharacter = {
  root: THREE.Object3D;
  mixer: THREE.AnimationMixer | null;
  /** GLB に含まれるアニメーション名一覧 */
  animationNames: string[];
  /** 現在再生中のアニメーション名 */
  currentAnimation: string | null;
  /** 次のアニメーションに切り替える（クロスフェード）。戻り値は新しい名前 */
  cycleAnimation: () => string | null;
  /** 名前指定で切り替え。一致しなければ no-op */
  playAnimation: (name: string) => boolean;
  dispose: () => void;
};

function countMeshes(obj: THREE.Object3D): number {
  let n = 0;
  obj.traverse((c) => {
    if ((c as THREE.Mesh).isMesh) n++;
  });
  return n;
}

function computeBBox(obj: THREE.Object3D): string {
  const box = new THREE.Box3().setFromObject(obj);
  if (box.isEmpty()) return 'empty';
  const size = new THREE.Vector3();
  box.getSize(size);
  const center = new THREE.Vector3();
  box.getCenter(center);
  return `size(${size.x.toFixed(2)}, ${size.y.toFixed(2)}, ${size.z.toFixed(2)}) center(${center.x.toFixed(2)}, ${center.y.toFixed(2)}, ${center.z.toFixed(2)})`;
}

/**
 * キャラ GLB をロードし、ボディ/顔テクスチャを差し替えてサイズスケールを適用する。
 * 本体 model-manager.ts の流儀を簡略移植。
 */
export async function buildCharacter(params: ARQrParams): Promise<BuiltCharacter> {
  const loader = getLoader();
  const [gltf, bodyTex, faceTex] = await Promise.all([
    loader.loadAsync(modelUrl(params.m, params.e ?? '')),
    loadTextureAsync(bodyTextureUrl(params.b)),
    loadTextureAsync(faceTextureUrl(params.f)),
  ]);

  const root = gltf.scene;
  const disposables: Array<{ dispose: () => void }> = [bodyTex, faceTex];

  console.log('[builder] loaded', {
    model: modelUrl(params.m),
    body: bodyTextureUrl(params.b),
    face: faceTextureUrl(params.f),
    children: root.children.length,
    meshCount: countMeshes(root),
    bbox: computeBBox(root),
  });

  // 本体 GameEngine.ts と同じマテリアル設定を適用する。
  // Body は cutout 透過 (transparent + alphaTest + depthWrite=true) にしないと、
  // GLB の alphaMode=BLEND 既定値のままだと深度書き込みされず、
  // face/手足が body を突き抜けて見える。
  root.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    node.frustumCulled = false; // SkinnedMesh の Box3 が安定しないため
    // AR 表示時に shadow catcher へ影を投影するため
    node.castShadow = true;
    node.receiveShadow = false;

    const mats = Array.isArray(node.material) ? node.material : [node.material];
    const isFace =
      (node.name || '').toLowerCase().includes('face') ||
      mats.some((m) => (m?.name || '').toLowerCase().includes('face'));

    for (const raw of mats) {
      const mat = raw as THREE.MeshStandardMaterial | undefined;
      if (!mat) continue;

      mat.color?.set(0xffffff);
      mat.emissive?.set(0x000000);

      if (isFace) {
        mat.map = faceTex;
        mat.transparent = true;
        mat.alphaTest = 0.5;
        mat.depthTest = true;
        mat.depthWrite = false;
        mat.side = THREE.FrontSide;
        node.renderOrder = 10;
      } else {
        mat.map = bodyTex;
        mat.transparent = true;
        mat.alphaTest = 0.5;
        mat.depthWrite = true;
      }
      mat.needsUpdate = true;
    }
  });

  // サイズスケール適用 (Y = height, XZ = width)
  const sH = SCALE_TABLE[Math.max(1, Math.min(5, params.h))] ?? 1;
  const sXZ = SCALE_TABLE[Math.max(1, Math.min(5, params.w))] ?? 1;
  root.scale.set(sXZ, sH, sXZ);

  // アニメーション準備
  const clips = gltf.animations;
  const animationNames = clips.map((c) => c.name);
  let mixer: THREE.AnimationMixer | null = null;
  let currentAction: THREE.AnimationAction | null = null;
  let currentAnimation: string | null = null;

  function _play(name: string): boolean {
    if (!mixer) return false;
    const clip = clips.find((c) => c.name === name);
    if (!clip) return false;
    const next = mixer.clipAction(clip);
    next.reset();
    next.setLoop(THREE.LoopRepeat, Infinity);
    next.enabled = true;
    next.fadeIn(0.2).play();
    if (currentAction && currentAction !== next) {
      currentAction.fadeOut(0.2);
    }
    currentAction = next;
    currentAnimation = name;
    return true;
  }

  if (clips.length > 0) {
    mixer = new THREE.AnimationMixer(root);
    const initial = clips.find((a) => /idle/i.test(a.name)) ?? clips[0];
    if (initial) _play(initial.name);
  }

  return {
    root,
    mixer,
    animationNames,
    get currentAnimation() {
      return currentAnimation;
    },
    cycleAnimation() {
      if (animationNames.length <= 1 || !currentAnimation) {
        return currentAnimation;
      }
      const idx = animationNames.indexOf(currentAnimation);
      const next = animationNames[(idx + 1) % animationNames.length]!;
      _play(next);
      return next;
    },
    playAnimation(name: string) {
      return _play(name);
    },
    dispose: () => {
      disposables.forEach((d) => d.dispose());
      root.traverse((node) => {
        if (node instanceof THREE.Mesh) {
          node.geometry?.dispose();
          const mat = node.material;
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
          else mat?.dispose();
        }
      });
    },
  } as BuiltCharacter;
}
